import type { AttentionReason, ColumnMapping, ColumnRole } from "@/lib/ingest";

export interface RemediationField {
  column: number;
  role: ColumnRole;
  label: string;
  placeholder: string;
}

const FIELD_COPY: Partial<Record<ColumnRole, Omit<RemediationField, "column" | "role">>> = {
  address_full: { label: "Address", placeholder: "123 Main Street, Springfield" },
  street: { label: "Street", placeholder: "123 Main Street" },
  city: { label: "City", placeholder: "Springfield" },
  zip: { label: "ZIP", placeholder: "90210" },
  lat: { label: "Latitude", placeholder: "40.7128" },
  lng: { label: "Longitude", placeholder: "-74.0060" },
};

const ADDRESS_ROLES: ColumnRole[] = ["city", "street", "address_full", "zip"];

/**
 * Whether the user has mapped enough spreadsheet structure for row-level fixes
 * to make sense. Without this, every row fails for the same dataset-wide reason
 * and the user must correct the column mapping instead of editing rows.
 */
export function hasUsableLocationMapping(mapping: ColumnMapping): boolean {
  if (mapping.mode === "coordinate") {
    return mapping.roles.lat !== undefined && mapping.roles.lng !== undefined;
  }
  return ADDRESS_ROLES.some((role) => mapping.roles[role] !== undefined);
}

/** Return only fields that already exist in the user's column mapping. */
export function remediationFields(
  mapping: ColumnMapping,
  reason: AttentionReason,
): RemediationField[] {
  const roles: ColumnRole[] =
    reason === "bad_coords" || reason === "out_of_range" ? ["lat", "lng"] : ADDRESS_ROLES;
  return roles.flatMap((role) => {
    const column = mapping.roles[role];
    const copy = FIELD_COPY[role];
    return column === undefined || !copy ? [] : [{ column, role, ...copy }];
  });
}

/** Field-local validation copy for the value currently shown in the form. */
export function remediationFieldError(
  reason: AttentionReason,
  field: RemediationField,
  value: string,
): string | null {
  const trimmed = value.trim();
  if (reason === "bad_coords" && (field.role === "lat" || field.role === "lng")) {
    if (trimmed !== "" && Number.isFinite(Number(trimmed))) return null;
    return field.role === "lat"
      ? "Enter a valid number for the latitude."
      : "Enter a valid number for the longitude.";
  }
  if (reason === "out_of_range") {
    const numeric = Number(trimmed);
    if (field.role === "lat" && (!Number.isFinite(numeric) || numeric < -90 || numeric > 90)) {
      return "Use a value from −90 to 90.";
    }
    if (field.role === "lng" && (!Number.isFinite(numeric) || numeric < -180 || numeric > 180)) {
      return "Use a value from −180 to 180.";
    }
  }
  return null;
}
